import { useQuery } from '@apollo/client/react';
import { useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';

import type {
  GetVerificationsQuery,
  GetVerificationsQueryVariables,
} from 'src/__generated__/graphql';
import { ROLE_REQUIREMENT_NAMESPACE } from 'src/components/RoleRequirementsForm/RoleRequirementsForm';
import { isFreeTextQuestion } from 'src/components/RoleRequirementsForm/utils';
import { useConfig } from 'src/context/config';
import { useJobId } from 'src/context/useJobId';
import { useAllQuestions } from 'src/hooks/useAllQuestions/useAllQuestions';
import { useCrossProductTransitionUrl } from 'src/hooks/useCrossProductTransitionUrl';
import { GET_VERIFICATIONS } from 'src/modules/graphql-client/queries/verifications.graphql';

const useGetVerifications = () => {
  const { config } = useConfig();
  const { jobId } = useJobId();
  const questionnaire = useWatch({ name: ROLE_REQUIREMENT_NAMESPACE });
  const selectedOptions = Object.values(questionnaire).flatMap(
    (options) => options,
  );
  const { data: questionsWithOptionIds } = useAllQuestions();

  const selectedAnswerUris = questionsWithOptionIds
    .flatMap((q) => (isFreeTextQuestion(q) ? [] : q.options))
    .flatMap(({ id, uri }) =>
      selectedOptions.includes(id) && uri ? [uri] : [],
    );

  const { data, loading } = useQuery<
    GetVerificationsQuery,
    GetVerificationsQueryVariables
  >(GET_VERIFICATIONS, {
    variables: {
      jobId,
      selectedAnswerUris,
      zone: config.ZONE,
    },
    fetchPolicy: 'cache-first',
  });

  const transformCrossProductTransitionUrl =
    useCrossProductTransitionUrl('seekpass');
  const [transformedData, setTransformedData] =
    useState<GetVerificationsQuery | null>(null);

  // Transform all ctaUrl values in verificationNudges
  useEffect(() => {
    const transformAllUrls = async () => {
      if (!data?.viewer?.verificationNudges) {
        setTransformedData(data || null);
        return;
      }

      const transformedNudges = await Promise.all(
        data.viewer.verificationNudges.map(async (nudge) => {
          if (!nudge.ctaUrl) {
            return nudge;
          }

          const transformedUrl = await transformCrossProductTransitionUrl(
            nudge.ctaUrl,
          );

          return {
            ...nudge,
            ctaUrl: transformedUrl,
          };
        }),
      );

      setTransformedData({
        ...data,
        viewer: {
          ...data.viewer,
          verificationNudges: transformedNudges,
        },
      });
    };

    transformAllUrls();
  }, [data, transformCrossProductTransitionUrl]);

  const verificationNudges = transformedData?.viewer?.verificationNudges || [];
  const rankVerifications =
    verificationNudges.find((nudge) => nudge.rank === 1) || null;
  const rankVerificationTypeSnakeCase =
    rankVerifications?.verificationType
      .map((type) => type.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase())
      .join(', ') || '';
  const hasDigitalIdentityNudge =
    verificationNudges.some((nudge) =>
      nudge.verificationType.includes('digital_identity'),
    ) || false;

  return {
    data: transformedData,
    loading: loading || !transformedData,
    rankVerifications,
    rankVerificationType: rankVerificationTypeSnakeCase,
    hasDigitalIdentityNudge,
  };
};

export { useGetVerifications };
